import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { Load, searchForLoad } from "@/api/receiver/searchForLoad";

interface SearchLoadDropdownProps {
  load?: string | null;
  loadName?: string | null;
  setLoad?: (load: string) => void;
  setLoadName?: (name: string) => void;
  disabled?: boolean;
}

export function SearchLoadDropdown({
  load,
  loadName,
  setLoad,
  setLoadName,
  disabled,
}: SearchLoadDropdownProps) {
  const [searchInput, setSearchInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const { data, isError, isLoading } = useQuery({
    queryKey: ["loads", searchInput],
    queryFn: () => searchForLoad({ searchQuery: searchInput }),
  });
  const filteredLoads = data;

  const selectLoad = (load: Load) => {
    setSearchInput(load.loadId);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef.current?.blur();
    // Update newClaim with selected load details
    if (setLoad && setLoadName) {
      setLoad(load.loadId);
      setLoadName(load.name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredLoads) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => {
        const newIndex = prev < filteredLoads.length - 1 ? prev + 1 : prev;
        if (suggestionsRef.current && newIndex >= 0) {
          const selectedElement = suggestionsRef.current.children[
            newIndex
          ] as HTMLElement;
          if (selectedElement) {
            selectedElement.scrollIntoView({
              behavior: "instant",
              block: "nearest",
            });
          }
        }
        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : 0;

        // Scroll to the selected item

        if (suggestionsRef.current && newIndex >= 0) {
          const selectedElement = suggestionsRef.current.children[
            newIndex
          ] as HTMLElement;
          if (selectedElement) {
            selectedElement.scrollIntoView({
              behavior: "instant",
              block: "nearest",
            });
          }
        }

        return newIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (
        selectedSuggestionIndex >= 0 &&
        filteredLoads[selectedSuggestionIndex]
      ) {
        selectLoad(filteredLoads[selectedSuggestionIndex]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
      inputRef.current?.blur();
    }
  };

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    setShowSuggestions(value.trim().length > 0);
    setSelectedSuggestionIndex(-1);
  };

  const handleInputFocus = () => {
    if (searchInput.trim().length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 300);
  };
  return (
    <div>
      <div className="relative">
        <Input
          ref={inputRef}
          id="add-store-input"
          type="text"
          value={load && loadName ? loadName : searchInput}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          placeholder="Start typing to search loads..."
          disabled={disabled}
          required
        />
      </div>
      {/* Suggestions Dropdown */}
      {showSuggestions && filteredLoads && filteredLoads.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredLoads.map((load, index) => (
            <div
              key={load.name}
              className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                index === selectedSuggestionIndex ? "bg-blue-50" : ""
              }`}
              onClick={() => selectLoad(load)}
            >
              <div className="flex flex-col">
                <span className="font-medium text-gray-900">
                  {load.name} - {load.loadState}
                </span>
                <span className="text-sm text-gray-500">
                  {load.sourceStationName} → {load.destinationStationName} |{" "}
                  {load.trailerNumber
                    ? `Trailer: ${load.trailerNumber}`
                    : "No trailer number"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Searching message */}
      {showSuggestions &&
        searchInput.trim() &&
        isLoading &&
        filteredLoads &&
        filteredLoads.length === 0 && (
          <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Searching for loads matching "{searchInput}"
            </div>
          </div>
        )}
      {/* No rddesults message */}
      {showSuggestions &&
        searchInput.trim() &&
        filteredLoads &&
        filteredLoads.length === 0 && (
          <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No loads found matching "{searchInput}"
            </div>
          </div>
        )}
    </div>
  );
}
